/**
 * Search utilities for the Client Workspace.
 *
 * Provides client-side search across project names with:
 * - Case-insensitive prefix and substring matching
 * - Relevance ranking (exact matches before partial matches)
 * - XSS-safe text highlighting via React elements (never dangerouslySetInnerHTML)
 * - Regex special character escaping
 * - Graceful handling of null/undefined fields
 *
 * The dataset is bounded (max 200 projects), so client-side search
 * is appropriate and avoids a backend search endpoint.
 *
 * @module search
 * @see Requirements 5.1, 5.2, 5.4
 */

import React from 'react';
import type { ClientProjectSummaryDto } from '../../../../types/client-workspace';

/** Maximum number of search results returned */
const MAX_RESULTS = 50;

/**
 * Escapes regex special characters in a string to prevent
 * ReDoS and unexpected matching behavior.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Searches projects by name using case-insensitive prefix/substring matching.
 *
 * Ranking:
 * 1. Exact matches (name equals query, case-insensitive)
 * 2. Prefix matches (name starts with query, case-insensitive)
 * 3. Substring matches (name contains query, case-insensitive)
 *
 * Returns at most 50 results.
 *
 * @param projects - Array of project summaries to search
 * @param query - Search query string (1-200 characters expected)
 * @returns Filtered and ranked array of matching projects
 */
export function searchProjects(
  projects: ClientProjectSummaryDto[],
  query: string,
): ClientProjectSummaryDto[] {
  // Empty or whitespace-only query returns no results
  const trimmed = query.trim();
  if (!trimmed) return [];

  const lowerQuery = trimmed.toLowerCase();

  const exact: ClientProjectSummaryDto[] = [];
  const prefix: ClientProjectSummaryDto[] = [];
  const substring: ClientProjectSummaryDto[] = [];
  const assetNameMatch: ClientProjectSummaryDto[] = [];
  const tagMatch: ClientProjectSummaryDto[] = [];

  for (const project of projects) {
    // Gracefully skip projects with null/undefined name
    const name = project.name;
    if (name == null) continue;

    const lowerName = name.toLowerCase();

    // Priority 1-3: project name matching
    if (lowerName === lowerQuery) {
      exact.push(project);
      continue;
    }
    if (lowerName.startsWith(lowerQuery)) {
      prefix.push(project);
      continue;
    }
    if (lowerName.includes(lowerQuery)) {
      substring.push(project);
      continue;
    }

    // Priority 4: asset name matching
    const assetNames = (project as any).assetNames as string[] | undefined;
    if (assetNames && assetNames.length > 0) {
      const matchesAssetName = assetNames.some(
        (assetName) => assetName?.toLowerCase().includes(lowerQuery),
      );
      if (matchesAssetName) {
        assetNameMatch.push(project);
        continue;
      }
    }

    // Priority 5: tag matching
    const assetTags = (project as any).assetTags as string[] | undefined;
    if (assetTags && assetTags.length > 0) {
      const matchesTag = assetTags.some(
        (tag) => tag?.toLowerCase().includes(lowerQuery),
      );
      if (matchesTag) {
        tagMatch.push(project);
        continue;
      }
    }
  }

  // Combine ranked results: exact → prefix → substring → asset name → tag, capped at MAX_RESULTS
  const results = [...exact, ...prefix, ...substring, ...assetNameMatch, ...tagMatch];
  return results.slice(0, MAX_RESULTS);
}

/**
 * Highlights matched text segments within a string by wrapping them
 * in `<mark>` React elements. Returns React nodes safe for rendering.
 *
 * Never uses dangerouslySetInnerHTML — all output is React elements.
 *
 * @param text - The text to highlight within
 * @param query - The search query to highlight
 * @returns React node with matched segments wrapped in <mark> elements
 */
export function highlightMatch(text: string, query: string): React.ReactNode {
  if (!text || !query) return text ?? '';

  const trimmed = query.trim();
  if (!trimmed) return text;

  // Escape regex special characters to prevent crashes
  const escaped = escapeRegex(trimmed);
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);

  // If no match found, return original text
  if (parts.length === 1) return text;

  return React.createElement(
    React.Fragment,
    null,
    ...parts.map((part, i) => {
      if (part.toLowerCase() === trimmed.toLowerCase()) {
        return React.createElement(
          'mark',
          { key: i, className: 'cw-search-highlight' },
          part,
        );
      }
      return part;
    }),
  );
}
